import { useEffect, useMemo, useRef, useState } from "react";
import { DownloadSimple, ShareNetwork, Copy, ImageSquare, FacebookLogo, InstagramLogo, TiktokLogo, LinkedinLogo, ChatCircle } from "@phosphor-icons/react";

// Single configurable constant so the template can be swapped later without touching sharing logic.
const TEMPLATE_URL = "/legacy-share/moving-for-template.png";
const LEGACY_URL = "https://sweatwithslay.com/legacy";

// Name box coordinates on the 941×1672 template (measured from the design):
// The cream card starts around y≈650 and ends around y≈1250. The name area
// sits below "I'M COMPLETING 911 MINUTES FOR" (y≈870) and above the red hairline
// (y≈1180) — that gives us a clean rectangle for the name.
const NAME_BOX = { x: 100, y: 900, w: 741, h: 250 };

function captionFor(name) {
  const n = (name || "").trim();
  if (n) {
    return `I'm moving for ${n}. This September, I'm completing 911 minutes of movement with the Sweat With Slay Legacy Challenge to honor the heroes, families, and legacies we carry with us. Join the movement: ${LEGACY_URL}`;
  }
  return `This September, I'm completing 911 minutes of movement with the Sweat With Slay Legacy Challenge to honor the heroes, families, and legacies we carry with us. Join the movement: ${LEGACY_URL}`;
}

async function renderGraphic(name) {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = TEMPLATE_URL;
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);
  const clean = (name || "").trim();
  if (clean) {
    // Auto-scale font — start big, shrink until it fits within NAME_BOX
    ctx.fillStyle = "#0A1E3F";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    let fontSize = 120;
    do {
      ctx.font = `900 ${fontSize}px "Impact","Anton","Arial Black",sans-serif`;
      const metrics = ctx.measureText(clean);
      if (metrics.width <= NAME_BOX.w - 40) break;
      fontSize -= 4;
    } while (fontSize > 28);
    ctx.fillText(clean, NAME_BOX.x + NAME_BOX.w / 2, NAME_BOX.y + NAME_BOX.h / 2);
  }
  return canvas;
}

export default function MovingForShare({ prefilledName }) {
  const [name, setName] = useState(prefilledName || "");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const previewRef = useRef(null);

  useEffect(() => { if (prefilledName) setName(prefilledName); }, [prefilledName]);

  const caption = useMemo(() => captionFor(name), [name]);

  const makePreview = async () => {
    setBusy(true); setFlash("");
    try {
      const canvas = await renderGraphic(name);
      setPreview(canvas.toDataURL("image/png"));
    } catch (e) {
      setFlash("Preview failed — reload and try again.");
    } finally { setBusy(false); }
  };

  const toBlob = () => new Promise(async (resolve) => {
    const canvas = await renderGraphic(name);
    canvas.toBlob((b) => resolve(b), "image/png");
  });

  const download = async () => {
    setBusy(true);
    try {
      const blob = await toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `im-moving-for${name ? "-" + name.replace(/[^a-z0-9]/gi, "-").toLowerCase() : ""}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setFlash("Graphic downloaded.");
    } finally { setBusy(false); }
  };

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); setFlash(`${label} copied.`); }
    catch { setFlash(`Copy failed — long-press to copy manually.`); }
  };

  const universalShare = async () => {
    setBusy(true); setFlash("");
    try {
      const blob = await toBlob();
      const file = new File([blob], "im-moving-for.png", { type: "image/png" });
      const shareData = { title: "Sweat With Slay Legacy Challenge", text: caption, url: LEGACY_URL, files: [file] };
      if (navigator.canShare?.({ files: [file] }) && navigator.share) {
        await navigator.share(shareData);
        setFlash("Shared.");
        return;
      }
      // Fallback: download + copy caption
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "im-moving-for.png";
      document.body.appendChild(a); a.click(); a.remove();
      try { await navigator.clipboard.writeText(caption); } catch { /* ignore */ }
      setFlash("Your graphic has been downloaded and your caption has been copied. Open the selected app, upload the image, and paste the caption.");
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      if (e?.name !== "AbortError") setFlash("Sharing not supported here — use Download + Copy caption instead.");
    } finally { setBusy(false); }
  };

  const platformShare = async (platform) => {
    setBusy(true); setFlash("");
    try {
      const blob = await toBlob();
      const url = URL.createObjectURL(blob);
      // Every platform gets the same package: download image + copy caption + open the platform link if we can prefill anything.
      const a = document.createElement("a"); a.href = url; a.download = "im-moving-for.png";
      document.body.appendChild(a); a.click(); a.remove();
      try { await navigator.clipboard.writeText(caption); } catch { /* ignore */ }
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      const enc = encodeURIComponent(caption);
      const encUrl = encodeURIComponent(LEGACY_URL);
      let openUrl = "";
      if (platform === "facebook") openUrl = `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${enc}`;
      else if (platform === "linkedin") openUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`;
      else if (platform === "sms") openUrl = `sms:?&body=${enc}`;
      else if (platform === "instagram") openUrl = "https://www.instagram.com/";
      else if (platform === "tiktok") openUrl = "https://www.tiktok.com/upload";
      if (openUrl) window.open(openUrl, "_blank", "noopener,noreferrer");
      const needsUpload = platform === "instagram" || platform === "tiktok";
      setFlash(
        needsUpload
          ? "Your graphic has been downloaded and your caption has been copied. Open Instagram/TikTok, upload the image, and paste the caption."
          : "Graphic downloaded and caption copied — paste into the post that just opened."
      );
    } finally { setBusy(false); }
  };

  useEffect(() => { if (previewRef.current && preview) previewRef.current.scrollIntoView({ block: "nearest" }); }, [preview]);

  const btn = "inline-flex items-center justify-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-bold border-2 transition-colors disabled:opacity-50";
  const btnGhost = `${btn} border-white/20 text-white/85 hover:border-[#B32027] hover:text-white`;
  const btnPrimary = `${btn} border-[#B32027] bg-[#B32027] text-white hover:bg-[#D42730]`;

  return (
    <div data-testid="moving-for-share" className="border-2 border-[#B32027]/40 bg-[#0F0F0F] p-5 md:p-6 space-y-5">
      <div>
        <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-[#B32027]">/ Share the challenge</p>
        <h3 className="font-display font-black uppercase tracking-tighter text-2xl md:text-3xl mt-1 leading-tight">
          Who are you<br /><span className="text-[#B32027]">moving for?</span>
        </h3>
        <p className="text-white/60 text-xs mt-2 leading-relaxed">Personalize the graphic with a name — or leave it blank. Downloadable + shareable on every platform.</p>
      </div>

      <label className="block">
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60">Who are you moving for?</span>
        <input
          data-testid="moving-for-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter a name or leave blank"
          maxLength={40}
          className="w-full mt-2 bg-[#0A0A0A] border border-white/20 focus:border-[#B32027] focus:outline-none px-4 py-3 text-white placeholder:text-white/30 text-sm"
        />
      </label>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={makePreview} disabled={busy} data-testid="moving-for-preview" className={btnPrimary}>
          <ImageSquare size={13} weight="bold" /> {preview ? "Update preview" : "Create my graphic"}
        </button>
        <button type="button" onClick={download} disabled={busy} data-testid="moving-for-download" className={btnGhost}>
          <DownloadSimple size={13} weight="bold" /> Download graphic
        </button>
        <button type="button" onClick={universalShare} disabled={busy} data-testid="moving-for-universal-share" className={btnGhost}>
          <ShareNetwork size={13} weight="bold" /> Share (device menu)
        </button>
      </div>

      {preview && (
        <div ref={previewRef} className="border border-white/10 bg-[#0A0A0A] p-3">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/50 mb-2">Preview</p>
          <img src={preview} alt="Moving for graphic preview" data-testid="moving-for-preview-img" className="w-full max-w-sm mx-auto border border-white/10" />
        </div>
      )}

      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60 mb-2">Share on…</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          <button type="button" onClick={() => platformShare("facebook")} disabled={busy} data-testid="share-facebook" className={btnGhost}><FacebookLogo size={13} weight="fill" /> Facebook</button>
          <button type="button" onClick={() => platformShare("instagram")} disabled={busy} data-testid="share-instagram" className={btnGhost}><InstagramLogo size={13} weight="fill" /> Instagram</button>
          <button type="button" onClick={() => platformShare("tiktok")} disabled={busy} data-testid="share-tiktok" className={btnGhost}><TiktokLogo size={13} weight="fill" /> TikTok</button>
          <button type="button" onClick={() => platformShare("linkedin")} disabled={busy} data-testid="share-linkedin" className={btnGhost}><LinkedinLogo size={13} weight="fill" /> LinkedIn</button>
          <button type="button" onClick={() => platformShare("sms")} disabled={busy} data-testid="share-sms" className={btnGhost}><ChatCircle size={13} weight="fill" /> Text</button>
        </div>
      </div>

      <div className="pt-3 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button type="button" onClick={() => copy(caption, "Caption")} data-testid="copy-caption" className={btnGhost}>
          <Copy size={13} weight="bold" /> Copy caption
        </button>
        <button type="button" onClick={() => copy(LEGACY_URL, "Legacy link")} data-testid="copy-link" className={btnGhost}>
          <Copy size={13} weight="bold" /> Copy legacy link
        </button>
      </div>

      {flash && <p data-testid="share-flash" className="text-[11px] text-emerald-300 leading-relaxed">{flash}</p>}
    </div>
  );
}
